import { HttpResourceRef } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, map, retry } from 'rxjs/operators';
import { IApiErrorResponse } from '@core/models/api-response.model';
import { IProfilo } from '@core/models/user.model';
import { ApiService } from '@core/services/api/api.service';
import { Console } from './logger.service';

/**
 * Servizio per la gestione dei profili utente.
 *
 * Questo servizio si occupa di recuperare l'elenco dei profili associati all'utente
 * autenticato tramite l'API Laravel.
 *
 * @example
 * constructor(private profilesService: ProfilesService) {}
 *
 * // Recupera i profili utente
 * this.profilesService.getProfiles().subscribe({
 *   next: (profiles) => console.log('Profili:', profiles),
 *   error: (error) => console.error('Errore:', error)
 * });
 */
@Injectable({
  providedIn: 'root'
})
export class ProfilesService {
  private readonly log_label = 'ProfilesService';
  private readonly apiService = inject(ApiService);

  /**
   * Resource reattiva per i profili utente (Angular 22 `httpResource`).
   * Per retry su deadlock MySQL usare ancora `getProfiles()` Observable.
   */
  createProfilesResource(shouldLoad: () => boolean): HttpResourceRef<IProfilo[]> {
    return this.apiService.getResource<IProfilo[]>(
      () => (shouldLoad() ? 'user/profiles' : undefined),
      undefined,
      { defaultValue: [], debugName: 'userProfiles' }
    );
  }

  /**
   * Recupera l'elenco dei profili dell'utente corrente.
   *
   * @returns Observable contenente una lista di profili utente
   * @throws {IApiErrorResponse} Se la richiesta fallisce
   */
  getProfiles(): Observable<IProfilo[]> {
    const log_pref = this.log_label + '.getProfiles() ';

    if (environment.ENABLE_DEBUG_ALERTS) {
      alert('auth-app : ProfilesService.getProfiles - INIZIO');
    }

    Console.log(`${log_pref}Recupero profili utente`, {}, 'info');

    return this.apiService.get<IProfilo[]>('user/profiles').pipe(
      // Retry automatico per errori 500 (deadlock MySQL)
      retry({
        count: 3,
        delay: (error: IApiErrorResponse, retryCount) => {
          const statusCode = (error as any)?.status_code ?? (error as any)?.status;
          const isDeadlock = statusCode === 500 &&
            (error?.message?.includes('Deadlock') || error?.message?.includes('deadlock'));

          if (isDeadlock && retryCount < 3) {
            const delayMs = (retryCount + 1) * 200; // 200ms, 400ms, 600ms
            if (environment.ENABLE_DEBUG_ALERTS) {
              alert(`auth-app : ProfilesService.getProfiles - Deadlock rilevato, retry ${retryCount + 1}/3 dopo ${delayMs}ms`);
            }
            return timer(delayMs);
          }
          return throwError(() => error);
        }
      }),
      map((profiles: IProfilo[]) => {
        if (environment.ENABLE_DEBUG_ALERTS) {
          alert('auth-app : ProfilesService.getProfiles - API chiamata con successo, numero profili: ' + (profiles?.length || 0));
        }
        return profiles;
      }),
      catchError((error: IApiErrorResponse) => {
        const errorMessage = log_pref + (error?.message || 'Errore nel recupero dei profili utente.');
        Console.log(errorMessage, error, 'err');
        if (environment.ENABLE_DEBUG_ALERTS) {
          alert('auth-app : ProfilesService.getProfiles - ERRORE API FINALE: ' + errorMessage + ' (status: ' + ((error as any)?.status_code || (error as any)?.status || 'sconosciuto') + ')');
        }
        return throwError(() => error);
      })
    );
  }
}
